import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  fetchMe,
  logout as logoutRequest,
  type MeResponse,
  switchWorkspace,
  type ThemeSetting,
  type Workspace,
} from "../lib/auth-api";
import type { ShellContext } from "../modules/types";

export const meQueryKey = ["auth", "me"] as const;

export function useMeQuery() {
  return useQuery({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 60_000 });
}

/** Only valid under AuthGate, which guarantees a session; throws otherwise so misuse fails loudly. */
export function useMe(): MeResponse {
  const { data } = useMeQuery();
  if (!data) throw new Error("useMe() used outside AuthGate");
  return data;
}

export function useWorkspace(): Workspace | null {
  const me = useMe();
  return me.workspaces.find((w) => w.id === me.activeWorkspaceId) ?? me.workspaces[0] ?? null;
}

export function isWorkspaceAdmin(ws: Workspace | null): boolean {
  // A missing role means the Worker has not sent the clone extension yet; owners are the common case then.
  return !ws?.role || ws.role === "OWNER" || ws.role === "ADMIN";
}

/** Inputs for sidebar `visible` rules. */
export function useShellContext(): ShellContext {
  const ws = useWorkspace();
  return useMemo(
    () => ({
      role: ws?.role ?? null,
      isAdmin: isWorkspaceAdmin(ws),
      workspaceSettings: ws?.workspaceSettings ?? {},
      features: ws?.features ?? [],
    }),
    [ws],
  );
}

export function useSwitchWorkspace() {
  const qc = useQueryClient();
  const me = useMe();
  return useMutation({
    mutationFn: (workspaceId: string) => switchWorkspace(me.user.id, workspaceId),
    onSuccess: (_data, workspaceId) => {
      qc.setQueryData<MeResponse | null>(meQueryKey, (prev) =>
        prev ? { ...prev, activeWorkspaceId: workspaceId } : prev,
      );
      // Every other query is workspace-scoped; drop them so pages refetch against the new workspace.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

const authChannelName = "clockify-auth";

/** Set while a deliberate logout is in flight so AuthGate does not record the page as a return URL. */
export const logoutState = { inProgress: false };

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      try {
        new BroadcastChannel(authChannelName).postMessage("logout");
      } catch {
        // BroadcastChannel is missing in some embedded browsers; the other tabs find out on their next request.
      }
      logoutState.inProgress = true;
      qc.setQueryData(meQueryKey, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      navigate("/login", { replace: true });
    },
  });
}

/** Other tabs follow a logout (shell spec 3.9). */
export function useLogoutBroadcast() {
  const qc = useQueryClient();
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(authChannelName);
      channel.onmessage = (e) => {
        if (e.data === "logout") qc.setQueryData(meQueryKey, null);
      };
    } catch {
      channel = null;
    }
    return () => channel?.close();
  }, [qc]);
}

export type ThemeName = "light" | "dark";

const themeStorageKey = "theme";

export function themeFromSetting(setting: ThemeSetting | undefined): ThemeName {
  return setting === "DARK" ? "dark" : "light";
}

export function applyTheme(theme: ThemeName) {
  document.documentElement.dataset.theme = theme;
  try {
    // index.html reads this before React mounts, so a reload does not flash the light theme.
    localStorage.setItem(themeStorageKey, theme);
  } catch {
    // Storage can be disabled; the theme still applies for this page view.
  }
}

/** Applies the user's theme (Preferences > Themes); light when logged out or unset. */
export function useThemeSync() {
  const { data } = useMeQuery();
  const theme = themeFromSetting(data?.settings?.theme ?? data?.user.settings?.theme);
  useEffect(() => {
    if (data === undefined) return;
    applyTheme(theme);
  }, [data, theme]);
}
